import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import api from '../../../lib/api';
import type { CheckpointPreview } from '../../../types/websiteWorkspace';
import { draftPreviewDocument } from './draftPreview';

export default function WebsiteReviewPreview() {
  const { projectId, checkpointId } = useParams();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<number | null>(null);
  const [width, setWidth] = useState('100%');
  const query = useQuery({
    queryKey: ['website-checkpoint-preview', projectId, checkpointId],
    queryFn: async () =>
      (
        await api.get<CheckpointPreview>(
          `/api/website-builder/projects/${projectId}/checkpoints/${checkpointId}/preview/`,
        )
      ).data,
    retry: false,
  });
  if (query.isLoading)
    return <CircularProgress aria-label="Loading shared review" />;
  if (query.isError || !query.data)
    return (
      <Alert color="warning">
        This review link is expired, revoked, or unavailable to your
        organization.
      </Alert>
    );
  const data = query.data;
  const page = data.pages.find((item) => item.id === selected) || data.pages[0];
  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography level="h2">{data.name}</Typography>
      <Typography>
        {data.label} · frozen draft · expires{' '}
        {new Date(data.expires_at).toLocaleString()}
      </Typography>
      <Alert color="neutral">
        Read-only review for signed-in teammates. This snapshot does not change
        when the working draft changes.
      </Alert>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Select
          aria-label="Review page"
          value={page?.id ?? null}
          onChange={(_, id) => setSelected(id)}
        >
          {data.pages.map((item) => (
            <Option key={item.id} value={item.id}>
              {item.title}
            </Option>
          ))}
        </Select>
        {[
          ['Desktop', '100%'],
          ['Tablet', '768px'],
          ['Mobile', '375px'],
        ].map(([label, value]) => (
          <Button
            key={label}
            variant={width === value ? 'solid' : 'outlined'}
            onClick={() => setWidth(value)}
          >
            {label}
          </Button>
        ))}
        <Button
          variant="plain"
          onClick={() =>
            navigate(
              `/marketing/website-builder/${projectId}/workspace?tab=review`,
            )
          }
        >
          Review current draft & leave notes
        </Button>
      </Box>
      {page && (
        <Box
          component="iframe"
          sandbox=""
          referrerPolicy="no-referrer"
          title={page.title}
          srcDoc={draftPreviewDocument(page.html, data.css, page.css)}
          sx={{
            width,
            maxWidth: '100%',
            height: '70vh',
            border: '1px solid',
            borderColor: 'divider',
            mx: 'auto',
          }}
        />
      )}
    </Stack>
  );
}
